import { deepStripNulls } from '@art-suite/art-core-ts';
import { compare } from 'fast-json-patch';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { usePageTitle } from '../../Lib';
import { FormikLabeledInput } from '../../Widgets';

import {
  getNormalizedScanTemplateRoot,
  initScanTemplateData,
  ScanTemplateRoot,
  ScanTemplateRootSchema,
  validateStandardTemplate,
} from '@kaiizen/kai-scan-template-system';
import { zodLessErrorMessage, zodLessParseOrUndefined } from '@kaiizen/zod-less';

type ValidationResult = {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  differences?: string[];
};

import { pxToRem } from '@/Client/Styles/pxToRem';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { PageLayout } from '../../Layout';
import { NoRecordsPlaceholder } from '../../Partials';
import { useAuth, useScanTemplates, useToast } from '../../State';
import { Button, DataTable, FlyOut, StyledForm } from '../../Widgets';
import { GenericLoadingPage } from '../GenericPages';

const CONTENT_CONTAINER_STYLE = {};

export const ScanTemplatesPage = () => {
  const { id } = useParams();
  const { orgId, userCan } = useAuth();
  const canCreateTemplate = userCan('create', 'ScanTemplate');
  const canUpdateTemplate = userCan('update', 'ScanTemplate');
  usePageTitle('Scan Templates');
  const { showToast, showErrorToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [fileError, setFileError] = useState('');
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const flyoutOpen = id === 'new';

  const showArchived = searchParams.get('archived') === 'true';
  const { scanTemplates, scanTemplateMutators, isLoading } = useScanTemplates({ archived: showArchived });

  useEffect(() => {
    if (!orgId) {
      navigate('/auth');
    }
  }, [orgId, navigate]);

  // Validate using the Standard Template validator
  const validateScanTemplate = (templateData: any): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    let differences: string[] = [];

    try {
      const normalizedTemplate = getNormalizedScanTemplateRoot(templateData);
      const standardizedTemplate = validateStandardTemplate(normalizedTemplate, { flattenLineItemsWithMultipleSkus: true });

      // If we get here, validation passed
      // Compare original with standardized to report differences
      const patches = compare(normalizedTemplate, standardizedTemplate);

      if (patches.length > 0) {
        warnings.push('Template was automatically standardized to comply with Standard Template specification');

        let missingPartsAdded: number = 0;
        // Convert patches to human-readable differences
        patches.forEach(patch => {
          const path = patch.path.replace(/\//g, ' → ').replace(/^[ →]+/, '');
          const oldValue = (patch as any).oldValue;
          switch (patch.op) {
            case 'add':
              missingPartsAdded++;
              break;
            case 'remove':
              differences.push(`Removed: ${path}`);
              break;
            case 'replace':
              if (oldValue === undefined) {
                missingPartsAdded++;
              } else {
                differences.push(`Changed: ${path} from ${JSON.stringify(oldValue)} to ${JSON.stringify(patch.value)}`);
              }
              break;
            case 'move':
              differences.push(`Moved: ${path} to ${JSON.stringify((patch as any).to)}`);
              break;
            case 'copy':
              differences.push(`Copied: ${path} to ${JSON.stringify((patch as any).to)}`);
              break;
          }
        });
        differences = [`${missingPartsAdded} missing parts added`, ...differences];
      }

      return {
        isValid: true,
        errors,
        warnings,
        differences: differences.length > 0 ? differences : undefined,
      };
    } catch (error) {
      // Extract meaningful error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown validation error';
      errors.push(errorMessage);

      return {
        isValid: false,
        errors,
        warnings,
        differences: undefined,
      };
    }
  };

  const handleFileValidation = async (file: File): Promise<ValidationResult> => {
    try {
      const fileContent = await file.text();
      const parsed = deepStripNulls(JSON.parse(fileContent));
      return validateScanTemplate(parsed);
    } catch (parseError) {
      return {
        isValid: false,
        errors: [`Invalid JSON format: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`],
        warnings: [],
      };
    }
  };

  const displayTemplates = scanTemplates
    ?.map(template => ({
      id: template.id,
      name: template.name,
      createdDate: new Date(template.createdAt).toLocaleDateString(),
      link: `/template/${template.id}`,
      menu: !canUpdateTemplate ? [] : [
        {
          label: template.archivedAt ? 'Unarchive' : 'Archive',
          action: async () => {
            const isArchiving = !template.archivedAt;
            await scanTemplateMutators.update(template.id, {
              archivedAt: template.archivedAt ? null : new Date().toISOString(),
            });
            trackEvent(isArchiving ? ANALYTICS_EVENTS.scanTemplateArchived : ANALYTICS_EVENTS.scanTemplateUnarchived, {
              templateId: template.id,
              name: template.name,
            });
          },
        },
      ],
    }));

  const haveTemplates = scanTemplates && scanTemplates.length > 0;

  if (isLoading) return <GenericLoadingPage title='Scan Templates' />;

  return (
    <PageLayout
      title={'Scan Templates' + (showArchived ? ' (archived)' : '')}
      contentLayoutStyling='standard'
      subHeaderButtons={
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Button
            color={showArchived ? 'gray' : 'bluePrimary'}
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              if (showArchived) {
                newParams.delete('archived');
              } else {
                newParams.set('archived', 'true');
              }
              setSearchParams(newParams);
            }}
          >
            {showArchived ? 'Show Active Templates' : 'Show Archived Templates'}
          </Button>
          {canCreateTemplate && (
            <Button
              color='bluePrimary'
              onClick={() => navigate('/templates/new')}
            >
              New Scan Template
            </Button>
          )}
        </div>
      }
    >
      {!haveTemplates
        ? (
          <NoRecordsPlaceholder
            recordType='Scan Templates'
            qualifier={showArchived ? 'Archived' : undefined}
            description={canCreateTemplate ? 'Create your first scan template to get started.' : 'No scan templates have been created yet.'}
            createButtonText={canCreateTemplate ? 'Add a Scan Template' : undefined}
            createFirstRecord={canCreateTemplate ? () => navigate('/templates/new') : undefined}
            iconType='templates'
          />
        )
        : (
          <div style={CONTENT_CONTAINER_STYLE}>
            <DataTable
              recordType='scan template'
              items={displayTemplates}
              defaultOrderBy='createdDate'
              defaultOrder='desc'
              columns={[
                { name: 'name', displayName: 'Scan Template', sortable: true },
                { name: 'createdDate', sortable: true, sortMethod: 'date', width: '120px' },
              ]}
            />
          </div>
        )}

      <FlyOut
        open={flyoutOpen}
        flyOutClosed={() => {
          navigate('/templates');
          setValidationResult(null);
          setFileError('');
          setIsValidating(false);
        }}
        title='Add a Scan Template'
      >
        <Formik
          initialValues={{ name: '', file: null }}
          onSubmit={async values => {
            if (!values.name.trim() || submitting) return;
            const name = values.name.trim();
            let templateData: ScanTemplateRoot = initScanTemplateData(name);
            if (values.file) {
              let parsed;
              try {
                const fileContent = await (values.file as File).text();
                parsed = deepStripNulls(JSON.parse(fileContent));
                const validated = zodLessParseOrUndefined(ScanTemplateRootSchema, parsed);
                if (!validated) {
                  setFileError('Invalid template JSON: ' + zodLessErrorMessage(ScanTemplateRootSchema, parsed));
                  return;
                }
                templateData = validated;
              } catch (error) {
                setFileError(error instanceof Error ? error.message : 'Invalid JSON or validation failed. Please check your file.');
                return;
              }
            }
            setSubmitting(true);
            try {
              const newTemplate = await scanTemplateMutators.create({ name }, templateData);
              trackEvent(ANALYTICS_EVENTS.scanTemplateCreated, {
                templateId: newTemplate.id,
                name: newTemplate.name,
                hasFile: !!values.file,
              });
              showToast('Scan template created successfully', 'success');
              navigate('/templates');
            } catch (error) {
              showErrorToast(error, 'create a scan template');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <StyledForm onSubmit={handleSubmit}>
              <span>
                Enter a name for your new scan template.
              </span>
              <FormikLabeledInput
                name='name'
                label='Name'
                placeholder='Enter scan template name'
                disabled={submitting}
              />
              <div className='file-input-container'>
                <input
                  key={fileInputKey}
                  type='file'
                  accept='.json'
                  disabled={submitting}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setFieldValue('file', file);
                      setFileError('');
                      setValidationResult(null);
                      setIsValidating(true);

                      // Auto-populate name field if it's empty
                      if (!values.name.trim()) {
                        const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
                        setFieldValue('name', fileNameWithoutExtension);
                      }

                      // Validate the file
                      try {
                        const result = await handleFileValidation(file);
                        setValidationResult(result);
                        if (!result.isValid) {
                          setFileError(`Validation failed: ${result.errors.join(', ')}`);
                        }
                      } catch (error) {
                        setFileError(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
                        setValidationResult({
                          isValid: false,
                          errors: ['Failed to validate file'],
                          warnings: [],
                        });
                      } finally {
                        setIsValidating(false);
                      }
                    } else {
                      setFieldValue('file', null);
                      setFileError('');
                      setValidationResult(null);
                      setFileInputKey(Date.now());
                    }
                  }}
                  className='input'
                />
                {values.file && (
                  <button
                    type='button'
                    className='clear-button'
                    disabled={submitting}
                    onClick={() => {
                      setFieldValue('file', null);
                      setFileError('');
                      setFileInputKey(Date.now());
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Validation loading indicator */}
              {isValidating && (
                <div
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: pxToRem(14),
                    color: '#6c757d',
                  }}
                >
                  <span className='spinner'></span>
                  Validating template...
                </div>
              )}

              <Button
                color='green'
                disabled={!values.name || submitting}
                type='submit'
              >
                {submitting
                  ? (
                    <>
                      <span className='spinner'></span>
                      Creating template...
                    </>
                  )
                  : (
                    'Create Scan Template'
                  )}
              </Button>
              {/* Validation Results */}
              {validationResult && !isValidating && (
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    backgroundColor: validationResult.isValid ? '#d4edda' : '#f8d7da',
                    border: `1px solid ${validationResult.isValid ? '#c3e6cb' : '#f5c6cb'}`,
                    color: validationResult.isValid ? '#155724' : '#721c24',
                    fontSize: pxToRem(14),
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {validationResult.isValid ? '✅ Validation Passed' : '❌ Validation Failed'}
                  </div>

                  {validationResult.errors.length > 0 && (
                    <div>
                      <strong>Errors:</strong>
                      <ol style={{ margin: '4px 0', paddingLeft: '16px', fontFamily: 'monospace' }}>
                        {validationResult.errors.map((error, index) =>
                          error.split('\n').map((line, lineIndex) => (
                            <li key={`${index}-${lineIndex}`} style={{ marginBottom: '2px' }}>
                              {line}
                            </li>
                          ))
                        )}
                      </ol>
                    </div>
                  )}

                  {validationResult.warnings.length > 0 && (
                    <div>
                      <strong>Warnings:</strong>
                      <ol style={{ margin: '4px 0', paddingLeft: '16px', fontFamily: 'monospace' }}>
                        {validationResult.warnings.map((warning, index) =>
                          warning.split('\n').map((line, lineIndex) => (
                            <li key={`${index}-${lineIndex}`} style={{ marginBottom: '2px' }}>
                              {line}
                            </li>
                          ))
                        )}
                      </ol>
                    </div>
                  )}

                  {validationResult.differences && validationResult.differences.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <strong>Changes Made:</strong>
                      <ol style={{ margin: '4px 0', paddingLeft: '16px', fontFamily: 'monospace', fontSize: pxToRem(12) }}>
                        {validationResult.differences.map((difference, index) => (
                          <li key={index} style={{ marginBottom: '2px' }}>
                            {difference}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </StyledForm>
          )}
        </Formik>
      </FlyOut>
    </PageLayout>
  );
};
